import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import uniqBy from 'lodash/uniqBy';

import { selectedBulkUpdateSbatGroupsSelector } from 'features/classCreation';
import Field from 'components/Field';
import FieldErrors from 'components/FieldErrors';
import styles from './RemoveSuperBatchesToggleChips.module.scss';
import ToggleChip from 'components/ToggleChip';
import SuperBatchSelect from 'components/SuperBatchSelect';

export default function RemoveSuperBatchesToggleChips({
  selectedSuperBatches,
  setSelectedSuperBatches,
}) {
  const selectedSbatGroups = useSelector(selectedBulkUpdateSbatGroupsSelector);
  const [invalidSelectedBatches, setInvalidSelectedBatches] = useState([]);

  const superBatches = useMemo(() => {
    const superBatches = [];

    Object.values(selectedSbatGroups).forEach((sbatGroup) => {
      const options = sbatGroup.super_batch_academy_topics.map((sbat) => ({
        value: sbat.super_batch.id,
        label: sbat.super_batch.name,
      }));
      superBatches.push(...options);
    });

    return uniqBy(superBatches, (obj) => obj.value);
  }, [selectedSbatGroups]);

  const handleAddSuperBatches = useCallback(
    (superBatch) => {
      setSelectedSuperBatches((prevSelectedSuperBatches) => [
        ...prevSelectedSuperBatches,
        superBatch,
      ]);
    },
    [setSelectedSuperBatches]
  );

  const handleRemoveSuperBatches = useCallback(
    (superBatch) => {
      setSelectedSuperBatches((prevSelectedSuperBatches) =>
        prevSelectedSuperBatches.filter((sb) => sb.value !== superBatch.value)
      );
    },
    [setSelectedSuperBatches]
  );

  const handleSuperBatchChange = useCallback(
    (selectedOptions) => {
      const invalidOptions = selectedOptions.filter(
        (option) => !superBatches.some((valid) => valid.value === option.value)
      );

      const invalidBatches = invalidOptions
        .map((option) => option.label)
        .join(', ');

      setInvalidSelectedBatches(invalidBatches);
      if (invalidOptions.length === 0) {
        setSelectedSuperBatches(selectedOptions);
      }
    },
    [superBatches, setSelectedSuperBatches]
  );

  const areSuperBatchesAdded = useCallback(
    (superBatch) =>
      selectedSuperBatches.some((sb) => sb.value === superBatch.value),
    [selectedSuperBatches]
  );

  return (
    <Field
      label="Select Batch/es to be removed"
      labelClassName={styles.root}
    >
      <SuperBatchSelect
        onChange={handleSuperBatchChange}
        value={selectedSuperBatches}
        className={styles.superBatchSelect}
        placeholder="Select Batches"
        isMulti
      />
      {invalidSelectedBatches.length > 0 && (
        <FieldErrors
          errors={`${invalidSelectedBatches} - not valid!`}
          className="m-t-xs"
        />
      )}
      {superBatches.map((superBatch) => (
        <ToggleChip
          key={superBatch.value}
          className="m-r-xxs m-t-xs h4"
          isAdded={areSuperBatchesAdded(superBatch)}
          onAdd={() => handleAddSuperBatches(superBatch)}
          onRemove={() => handleRemoveSuperBatches(superBatch)}
          label={superBatch.label}
        />
      ))}
    </Field>
  );
}
